import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { CollectionSelectorForm } from 'elements/MemoForm/forms/CollectionSelectorForm';
import { MemoTypeSelectorForm } from 'elements/MemoForm/forms/MemoTypeSelectorForm';
import { Collection } from 'models/Collection';
import { AnyMemo, Task } from 'models/Memo';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKey } from 'core/react-query/queryClient';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import { discardDraft } from 'xstate/actors/DraftActor';
import { discardComposerDraft } from 'xstate/actors/ComposerDraftActor';
import { CancelBtn } from './buttons/CancelBtn';
import { ExpandCollapseBtn } from './buttons/ExpandCollapseBtn';
import { ResizeHandle } from './buttons/ResizeHandle';
import { SubmitBtn } from './buttons/SubmitBtn';
import { ToggleBtn } from './buttons/ToggleBtn';
import { MemoContentForm } from './MemoContentForm';
import { ComposerLauncher } from './ComposerLauncher';
import { TaskProgress } from './TaskProgress';
import { FormContextProvider } from './context/FormContext';
import {
  defaultFormValues,
  useFormInitialisation,
} from './hooks/useFormInitialisation';
import { useFormSubmission } from './hooks/useFormSubmission';
import { useTitle } from './hooks/useTitle';
import { useDescription } from './hooks/useDescription';
import { useSource } from './hooks/useSource';
import { DateChip } from './fields/DateChip';
import {
  useComposerExpansion,
  MIN_EXPANDED_HEIGHT,
} from './hooks/useComposerExpansion';
import { useComposerDraftAutosave } from './hooks/useComposerDraftAutosave';
import { useFocusWithin } from 'hooks/useFocusWithin';
import { useTranslations } from 'next-intl';

export enum FormMode {
  Create,
  Edit,
}

interface Props<T extends AnyMemo> {
  memo?: T | null;
  defaultCollection?: Collection;
  // The day a new memo is dated to unless the user picks one, e.g. the
  // Journal day that is open. Nothing: the server dates it to now.
  defaultDatedAt?: Date | null;
  onSuccess?: () => void;
  onCancel?: () => void;
  expandable?: boolean;
}

export const MemoForm = <T extends AnyMemo>({
  memo = null,
  defaultCollection = null,
  defaultDatedAt = null,
  onSuccess,
  onCancel,
  expandable = false,
}: Props<T>) => {
  const t = useTranslations();
  const formRef = useRef<HTMLFormElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const { selectedBox, selectedCollection } = useActiveSpace();
  const boxId = selectedBox?.id;
  const methods = useFormInitialisation({ memo, defaultCollection, boxId });
  const { watch, handleSubmit, reset, getValues } = methods;
  const queryClient = useQueryClient();

  const {
    isFullyExpanded,
    setIsFullyExpanded,
    expandedHeight,
    setExpandedHeight,
    maxExpandedHeight,
    handleExpandCollapse,
  } = useComposerExpansion(expandable, formRef, editorRef);

  // Opened by hand from the resting row. Some types (images, documents)
  // have no text field to type in, so a click alone must be enough to open
  // the composer and show its controls. Cleared again once focus leaves.
  const [isOpened, setIsOpened] = useState(false);

  const {
    isFocusedWithin,
    setIsFocusedWithin,
    handleFormFocus,
    handleFormBlur,
  } = useFocusWithin({
    isWithin: (target) => Boolean(target.closest('[data-headlessui-portal]')),
    onLeave: () => setIsOpened(false),
  });

  // Once the resting row has opened the composer, put the caret in its
  // first field. Types without one focus their first button instead, so
  // that leaving the composer still closes it.
  useEffect(() => {
    if (!isOpened) {
      return;
    }
    editorRef.current
      ?.querySelector<HTMLElement>('textarea, input:not([type="file"]), button')
      ?.focus();
  }, [isOpened]);

  const { cancelPendingSave } = useComposerDraftAutosave({
    memo,
    boxId,
    watch,
    getValues,
  });

  const { isSubmitting, onSubmit } = useFormSubmission({
    defaultDatedAt,
    onSuccess: () => {
      discardDraft(memo?.id);
      cancelPendingSave();
      discardComposerDraft(boxId);

      if (selectedBox) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.MEMOS, selectedBox?.id],
        });
      }

      const { type, collectionId } = getValues();
      reset(
        {
          ...defaultFormValues(memo, defaultCollection),
          type,
          collectionId: collectionId ?? selectedCollection?.id,
        },
        {
          keepDirty: false,
          keepTouched: false,
          keepErrors: false,
          keepSubmitCount: false,
        },
      );

      if (expandable) {
        setIsFullyExpanded(false);
        setIsFocusedWithin(false);
        setIsOpened(false);
      }

      onSuccess?.();
    },
  });

  const selectedMemoType = watch('type');

  const { isTitleVisible, canShowTitle, toggleTitle } = useTitle({
    memo,
    methods,
    selectedMemoType,
  });

  const { isDescriptionVisible, canShowDescription, toggleDescription } =
    useDescription({
      memo,
      methods,
      selectedMemoType,
    });

  const { isSourceVisible, canShowSource, toggleSource } = useSource({
    memo,
    methods,
    selectedMemoType,
  });

  const watchedTasks = watch('tasks') as unknown as Task[] | undefined;
  const hasTaskContent = Boolean(watchedTasks?.some((task) => task?.content));
  const hasDirtyContent = Boolean(
    watch('content') ||
    watch('title') ||
    watch('description') ||
    watch('source') ||
    watch('name') ||
    hasTaskContent ||
    watch('imageIds')?.length ||
    watch('audioIds')?.length ||
    watch('videoIds')?.length ||
    watch('fileIds')?.length,
  );
  const hasCollectionOverride =
    (watch('collectionId') ?? null) !== (defaultCollection?.id ?? null);
  // A chosen date shows on its pill, so it counts as an active chip too.
  const hasDate = Boolean(watch('datedAt'));
  const hasActiveChips =
    isTitleVisible || isDescriptionVisible || isSourceVisible || hasDate;

  // In use: opened by hand, focused, holding content, chips or the expanded
  // layout. Otherwise the composer rests as a single row.
  const isInUse =
    !expandable ||
    isOpened ||
    isFocusedWithin ||
    hasDirtyContent ||
    hasCollectionOverride ||
    hasActiveChips ||
    isFullyExpanded;

  // The full form and its resting row are both always mounted so the fields
  // keep their state; only one of them shows.
  const formVisibility = isInUse ? 'block' : 'hidden';

  // A press on anything that can't take focus (blank space between the
  // chips, a label, a switch's caption) would move focus to the page, and
  // the composer would take that for leaving and fold back to the resting
  // row. Keep focus where it is instead; a label still activates its
  // control on the click that follows. Controls and the type picker's
  // portal keep their own focus.
  const handleFormMouseDown = (event: React.MouseEvent<HTMLFormElement>) => {
    const target = event.target as HTMLElement;
    if (!formRef.current?.contains(target)) {
      return;
    }
    if (
      target.closest('button, input, textarea, select, a, [role="separator"]')
    ) {
      return;
    }
    event.preventDefault();
  };

  const handleFormClick = (event: React.MouseEvent<HTMLFormElement>) => {
    // A click on blank space around the resting row opens the composer too.
    if (isInUse) {
      return;
    }
    const target = event.target as HTMLElement;
    if (
      target.closest('button, input, textarea, select, a, [role="separator"]')
    ) {
      return;
    }
    setIsOpened(true);
  };

  const formContextValue = {
    ...methods,
    mode: memo ? FormMode.Edit : FormMode.Create,
    isSubmitting,
    selectedMemoType,
    isTitleVisible,
    canShowTitle,
    toggleTitle,
    isDescriptionVisible,
    canShowDescription,
    toggleDescription,
    isSourceVisible,
    canShowSource,
    toggleSource,
    isFullyExpanded,
  };

  return (
    <FormContextProvider value={formContextValue}>
      <form
        ref={formRef}
        className={styles.container}
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        onFocus={handleFormFocus}
        onBlur={handleFormBlur}
        onClick={handleFormClick}
        onMouseDown={handleFormMouseDown}
      >
        {!isInUse && <ComposerLauncher onOpen={() => setIsOpened(true)} />}

        {isFullyExpanded && (
          <ResizeHandle
            height={expandedHeight}
            onChange={setExpandedHeight}
            onCollapse={() => setIsFullyExpanded(false)}
            min={MIN_EXPANDED_HEIGHT}
            max={maxExpandedHeight}
          />
        )}

        <div
          data-testid="composer-chrome-header"
          className={classNames(styles.header, formVisibility)}
        >
          <MemoTypeSelectorForm />

          <span className={styles.headerDivider} aria-hidden="true" />

          <div className={styles.chips}>
            <CollectionSelectorForm />

            <ToggleBtn
              label={t('memoComposer.title')}
              isActive={isTitleVisible}
              canToggle={canShowTitle}
              onToggle={toggleTitle}
            />

            <ToggleBtn
              label={t('memoComposer.description')}
              isActive={isDescriptionVisible}
              canToggle={canShowDescription}
              onToggle={toggleDescription}
            />

            <ToggleBtn
              label={t('memoComposer.source')}
              isActive={isSourceVisible}
              canToggle={canShowSource}
              onToggle={toggleSource}
            />

            {/* Every memo has a date, so this pill is always last. */}
            <DateChip defaultDatedAt={defaultDatedAt} />
          </div>

          {expandable && (
            <ExpandCollapseBtn
              isFullyExpanded={isFullyExpanded}
              onClick={handleExpandCollapse}
            />
          )}
        </div>

        <div
          data-testid="composer-body"
          className={classNames(styles.body.container, formVisibility)}
        >
          <div
            ref={editorRef}
            className={isFullyExpanded ? styles.body.scroll : undefined}
            style={isFullyExpanded ? { height: expandedHeight } : undefined}
          >
            <MemoContentForm memo={memo} />
          </div>
        </div>

        <div className={classNames(styles.body.btn.container, formVisibility)}>
          <TaskProgress watch={watch} />

          <div className={styles.body.btn.actions}>
            <CancelBtn onCancel={onCancel} />
            <SubmitBtn />
          </div>
        </div>
      </form>
    </FormContextProvider>
  );
};

const styles = {
  // Chromeless: the caller owns the surface (border/rounding/shadow). The
  // desktop composer wraps this in a card; the mobile modal is the surface.
  container: 'block bg-surface-card text-ink-primary',
  header: 'flex items-center gap-2.5 px-[18px] pt-3.5 pb-3',
  headerDivider: 'h-5 w-px shrink-0 bg-stroke',
  chips: 'flex flex-1 flex-wrap items-center gap-2',
  body: {
    container: 'px-[18px] pb-3',
    // Scrolling clips anything drawn outside the fields, like the link
    // field's focus ring. Pull the edges out and pad back in so the ring has
    // room without moving the content.
    scroll: 'overflow-y-auto -mx-1 px-1 -my-1 py-1',
    btn: {
      container: 'flex items-center justify-between px-[18px] pt-2 pb-3.5',
      actions: 'flex items-center ml-auto',
    },
  },
};
